import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {EcommerceConnectionService} from '../../../service-data/ecommerce-connection.service';
import {LanguagePipe} from '../../../pipe/language.pipe';

/**
 * Optional payload for the Connect Shopify dialog.
 *
 * @property returnTo Override the post-callback SPA URL the server
 *   should redirect the browser back to after finalization. Defaults
 *   to `window.location.origin + '/secure/ecommerce'`, which is the
 *   page hosting the matrix-param handoff in
 *   `EcommerceComponent.ngOnInit()`.
 */
export interface ConnectShopifyDialogData {
  returnTo?: string;
}

/**
 * Small Material dialog that drives the
 * `EcommerceConnectionService.shopifyInstallStart(...)` call and
 * hands the browser off to Shopify's hosted authorize page.
 *
 * Flow:
 *   1. Operator types a shop handle (e.g. `my-store` or
 *      `my-store.myshopify.com`). We accept either form and let the
 *      server normalize.
 *   2. POST to `/1.0/ui/ecommerceconnection/shopify/start`.
 *   3. On success, top-level navigate to the returned `authorizeUrl`.
 *      The dialog is intentionally NOT closed — the browser leaves
 *      the SPA before any subsequent state change matters.
 *   4. On error, render the message inline so the operator can fix
 *      the shop handle and retry without losing the dialog. We
 *      deliberately do NOT use `AlertService` here.
 *
 * See `docs/contracts/shared/ShopifyAppInstall.md` for the
 * contract this dialog drives.
 */
@Component({
  selector: 'app-connect-shopify-dialog',
  templateUrl: './connect-shopify-dialog.component.html',
  styleUrls: ['./connect-shopify-dialog.component.css']
})
export class ConnectShopifyDialogComponent {
  /** User-entered shop handle. Accepted in either `my-store` or `my-store.myshopify.com` form. */
  shop: string = '';
  /** True while the start request is in flight. Disables the submit button + input. */
  submitting: boolean = false;
  /** Inline error surface. Empty string means "no error". */
  errorMessage: string = '';

  private readonly returnTo: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: ConnectShopifyDialogData,
    private dialogRef: MatDialogRef<ConnectShopifyDialogComponent>,
    private ecommerceConnectionService: EcommerceConnectionService
  ) {
    // Allow callers to override returnTo (mostly useful in tests).
    // The default lands the browser back on the Ecommerce settings
    // page, where `EcommerceComponent.ngOnInit()` consumes the
    // `ecommerceConnectionId` matrix param queued by
    // `AppComponent.captureExternalCallbackParams()`.
    if (data && data.returnTo) {
      this.returnTo = data.returnTo;
    } else if (typeof window !== 'undefined' && window.location && window.location.origin) {
      this.returnTo = window.location.origin + '/secure/ecommerce';
    } else {
      // Last-resort fallback (server-side rendering / tests). The
      // server treats a missing returnTo as "redirect to the SPA
      // root" — acceptable but not as good as the canonical URL.
      this.returnTo = '';
    }
  }

  /** True when the shop handle looks plausibly valid (non-empty after trim). */
  get canSubmit(): boolean {
    return !this.submitting && !!this.shop && this.shop.trim().length > 0;
  }

  onCancel(): void {
    if (this.submitting) { return; }
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    if (!this.canSubmit) { return; }
    this.submitting = true;
    this.errorMessage = '';

    const payload: { shop: string; returnTo?: string } = { shop: this.shop.trim() };
    if (this.returnTo) {
      payload.returnTo = this.returnTo;
    }

    this.ecommerceConnectionService.shopifyInstallStart(payload).subscribe(
      response => {
        if (response && response.authorizeUrl) {
          // Top-level navigation. Do NOT close the dialog first —
          // the SPA is about to be replaced by Shopify's authorize
          // page, and closing would briefly flash the underlying
          // settings page while the browser navigates.
          window.location.href = response.authorizeUrl;
        } else {
          this.submitting = false;
          this.errorMessage = LanguagePipe.get('Shopify did not return an authorize URL. Please try again.');
        }
      },
      error => {
        this.submitting = false;
        this.errorMessage = this.extractErrorMessage(error);
        console.error('ConnectShopifyDialog: shopifyInstallStart failed', error);
      }
    );
  }

  /**
   * Surface the most useful message from an HTTP error response.
   * Prefers structured `error.error.error` payloads (the
   * server-side `{ error: string }` shape used throughout the API),
   * falling back to `error.message`, and finally to a generic
   * localized fallback.
   */
  private extractErrorMessage(err: any): string {
    if (err && err.error && typeof err.error === 'object' && err.error.error) {
      return String(err.error.error);
    }
    if (err && err.message) {
      return String(err.message);
    }
    return LanguagePipe.get('Could not start the Shopify install. Please verify the shop handle and try again.');
  }
}
